import { Theme } from '@mui/material/styles';

/**
 * Unselected tab labels, which MUI paints in `text.secondary`.
 *
 * On the dark palette that token is GREY[500] (#919EAB), and against the dark card surface
 * (GREY[800], #212B36) it lands at 5.2:1 — technically AA, but a muted slate-on-slate that is genuinely
 * hard to pick out, especially on a six-tab bar where only the selected one is white. Lifting it to
 * GREY[300] takes it to roughly 11:1 and makes the unselected tabs legible without making them shout
 * over the selected one, which stays `text.primary`.
 *
 * Light mode is deliberately untouched: GREY[600] on white is 4.9:1, which is fine against a bright
 * surface and is the contrast the light theme was designed around.
 */
const unselectedTabColor = (theme: Theme) =>
  theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.text.secondary;

export default function Tabs(theme: Theme) {
  return {
    MuiTab: {
      styleOverrides: {
        root: {
          padding: 0,
          fontWeight: theme.typography.fontWeightMedium,
          borderTopLeftRadius: theme.shape.borderRadius,
          borderTopRightRadius: theme.shape.borderRadius,
          color: unselectedTabColor(theme),
          '&.Mui-selected': {
            color: theme.palette.text.primary
          },
          '&:not(:last-of-type)': {
            marginRight: theme.spacing(5)
          },
          '@media (min-width: 600px)': {
            minWidth: 48
          }
        },
        labelIcon: {
          minHeight: 48,
          flexDirection: 'row',
          '& > *:first-of-type': {
            marginBottom: 0,
            marginRight: theme.spacing(1)
          }
        },
        wrapper: {
          flexDirection: 'row',
          whiteSpace: 'nowrap'
        },
        textColorInherit: {
          opacity: 1,
          color: unselectedTabColor(theme)
        }
      }
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          padding: 0
        }
      }
    },
    MuiTabScrollButton: {
      styleOverrides: {
        root: {
          width: 48,
          borderRadius: '50%'
        }
      }
    }
  };
}
